import type {
  CalendarClient,
  CalendarEvent,
  Calendar as CalendarModel,
  Principal,
} from '@wtfalch/calendar/calendar';
import {
  Button,
  Field,
  FreeBusy,
  type FreeBusyPerson,
  type FreeBusySlot,
  Icon,
  Identity,
  Input,
  Panes,
  Segmented,
  SegmentedOption,
  Select,
  Textarea,
  Toggle,
} from '@wtfalch/design';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createEventCall, type EventCallSetup, endEventCall } from '../calls/eventCallSetup.ts';
import { parseGuestEntry } from '../calls/eventCalls.ts';
import type { RoomCallBinding } from '../calls/roomCalls.ts';

import { Avatar } from '../ui/Avatar.tsx';
import {
  durationMinutes,
  freeBusyBands,
  ownFreeBusyBands,
  statusAt,
  toIsoDuration,
} from './layout.ts';
import type { Identity as ViewerIdentity } from './participants.ts';
import { useEscape } from './useEscape.ts';

type Attendee = { principal: Principal; bands: FreeBusyPerson['slots'] | null };

const DURATIONS = [15, 30, 60] as const;
/** The hours the free/busy axis shows at least; it widens to take in the
 *  chosen time. */
const HOURS = { start: 9, end: 17 };
/** Every IANA zone the runtime knows, for the time zone picker -- the issue
 *  (agora#168, MSG-065) asks for this list by name rather than a hand-kept
 *  one that would drift from the runtime's own. */
const TIME_ZONES = Intl.supportedValuesOf('timeZone');

function dateParam(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A wall-clock date and time as it is meant in a specific IANA zone,
 *  turned into the real instant it names. There is no `Temporal` yet, so
 *  this is the standard offset-guess-and-correct trick: format a first UTC
 *  guess back through the target zone, then shift the guess by however far
 *  it drifted. Two passes: on a DST-change day the first correction can
 *  land on the other side of the change (a 02:15 fall-back pick read back
 *  as 01:15), and the second pass takes the offset at the corrected
 *  instant. */
function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const format = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  // How far the zone's wall clock is ahead of UTC at `instant`.
  const offsetAt = (instant: number) => {
    const parts = format.formatToParts(new Date(instant));
    const read = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    const wall = Date.UTC(
      read('year'),
      read('month') - 1,
      read('day'),
      read('hour'),
      read('minute'),
      read('second'),
    );
    return wall - instant;
  };
  const first = guess - offsetAt(guess);
  const second = guess - offsetAt(first);
  if (second + offsetAt(second) === guess) return new Date(second);
  // A time the clock skips (02:30 on a spring-forward day) does not exist;
  // move it forward, as calendar apps do.
  return new Date(Math.max(first, second));
}

/** The grid's hours mean hours *in `timeZone`*, not the browser's -- a
 *  person who picked a zone other than their own is reading someone else's
 *  business hours, not their own (agora#168 review finding: the grid and
 *  the availability fetch both used to stay in the browser's zone no
 *  matter what was picked, so a change of zone silently asked and answered
 *  the wrong question). */
function daySpan(
  dateStr: string,
  timeZone: string,
  hours: { start: number; end: number },
): { start: Date; end: Date } {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) {
    const now = new Date();
    return { start: now, end: now };
  }
  return {
    start: zonedTimeToUtc(y, m, d, hours.start, 0, timeZone),
    end: zonedTimeToUtc(y, m, d, hours.end, 0, timeZone),
  };
}

/** `layout.ts`'s own `eventTimeLabel`, but reading `start`/`end` in
 *  `timeZone` rather than the browser's -- the picked-slot summary below
 *  the grid has to read like the grid above it (agora#168 review finding:
 *  after picking a non-browser zone, this line kept reading the picked
 *  instant back in the browser's own zone, so a typed 10:00 New York pick
 *  showed as "16:00" from Oslo). Kept local rather than added to
 *  `layout.ts`, which has no zone parameter on any of its own formatters
 *  and is shared by other screens that never pick a zone. */
function pickedTimeLabel(start: Date, end: Date, timeZone: string): string {
  const weekdayDate = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone,
  });
  const time = new Intl.DateTimeFormat('en-GB', { timeStyle: 'short', timeZone });
  return `${weekdayDate.format(start)} · ${time.format(start)} – ${time.format(end)}`;
}

export function FindTime({
  client,
  identity,
  meName,
  calendars,
  initialDate,
  onCancel,
  onCreated,
  onError,
  callUrl,
  calls,
}: {
  client: CalendarClient;
  identity: ViewerIdentity | null;
  /** The signed-in person's own name, from the mail SDK's identities (the
   *  same name `AgoraShell`'s account chip and Today's greeting use) --
   *  `CalendarIdentity` itself carries no name (agora#1, item 15's own
   *  Finding), so this is passed in from outside rather than read off
   *  `identity`. */
  meName: string | null;
  calendars: readonly CalendarModel[];
  initialDate: Date;
  onCancel: () => void;
  onCreated: (event: CalendarEvent) => void;
  onError: (error: unknown) => void;
  /** Present only when calls are on: builds the join URL for an event uid,
   *  and its presence is what shows "Add a call". */
  callUrl?: (uid: string) => string;
  /** The calls service, for the session and guest link a call needs. */
  calls?: RoomCallBinding;
}) {
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [addCall, setAddCall] = useState(false);
  const [mayInviteOthers, setMayInviteOthers] = useState(false);
  const [mayInviteSelf, setMayInviteSelf] = useState(false);
  const [dateStr, setDateStr] = useState(() => dateParam(initialDate));
  const [duration, setDuration] = useState<(typeof DURATIONS)[number]>(30);
  const [timeStr, setTimeStr] = useState(defaultTime);
  // Defaults to the browser's own zone (agora#168, MSG-065); the picker
  // below lets a person choose a different one before sending.
  const [timeZone, setTimeZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [attendeeInput, setAttendeeInput] = useState('');
  const [attendeeError, setAttendeeError] = useState<string | null>(null);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [sending, setSending] = useState(false);
  // The events of the chosen day, for your own row (`null` until they land).
  const [ownEvents, setOwnEvents] = useState<readonly CalendarEvent[] | null>(null);

  // The axis is 09 to 17, widened to hold the chosen time: a 07:30 meeting
  // would otherwise sit off the end of it, with nothing drawn for it.
  const [pickedHour, pickedMinute] = timeStr
    ? (timeStr.split(':').map(Number) as [number, number])
    : [HOURS.start, 0];
  const hoursStart = Math.min(HOURS.start, pickedHour);
  const hoursEnd = Math.min(
    24,
    Math.max(HOURS.end, Math.ceil(pickedHour + (pickedMinute + duration) / 60)),
  );
  const hours = useMemo(() => ({ start: hoursStart, end: hoursEnd }), [hoursStart, hoursEnd]);
  const hourLabels = Array.from({ length: hours.end - hours.start }, (_, i) =>
    String(hours.start + i).padStart(2, '0'),
  );
  const span = useMemo(() => daySpan(dateStr, timeZone, hours), [dateStr, timeZone, hours]);
  const ownCalendar = calendars.find(
    (c) => c.myRights.includes('mayWriteAll') || c.myRights.includes('mayWriteOwn'),
  );

  const loadBands = useCallback(
    (accountId: string) =>
      client
        .getAvailability([accountId], {
          after: span.start.toISOString(),
          before: span.end.toISOString(),
        })
        // calendar 0.2.0: getAvailability batches every requested principal
        // into one `Map`, and distinguishes a real (if empty) read from one
        // that could not be made (`AvailabilityResult`'s own docblock).
        // `freeBusyBands` already treats an empty periods list as "unknown"
        // (its own doc comment), so an unread principal here reproduces
        // exactly what an empty answer meant before this bump.
        .then((results) => {
          const result = results.get(accountId);
          return freeBusyBands(result?.kind === 'known' ? result.periods : [], span);
        }),
    [client, span],
  );

  // Your own row, from your own events in the chosen day's span -- never
  // `getAvailability` on yourself. Busy where you have an event, free
  // everywhere else in the span; never "unknown", the way an empty
  // `getAvailability` answer would have to be read (see `freeBusyBands`'s
  // own docblock) -- your own calendar is never "not shared with you". The
  // span is in the picked zone, so the events are asked for by instant, not
  // by the browser's idea of the date.
  useEffect(() => {
    let live = true;
    setOwnEvents(null);
    client
      .listEvents({ after: span.start.toISOString(), before: span.end.toISOString() })
      .then((list) => {
        if (live) setOwnEvents(list);
      })
      .catch(onError);
    return () => {
      live = false;
    };
  }, [client, span, onError]);

  const ownBands: FreeBusySlot[] = useMemo(() => {
    const periods = (ownEvents ?? []).map((e) => {
      const start = new Date(e.start);
      const end = new Date(start.getTime() + durationMinutes(e.duration) * 60_000);
      return { start: start.toISOString(), end: end.toISOString() };
    });
    return ownFreeBusyBands(periods, span);
  }, [ownEvents, span]);

  // Reload every added attendee's bands when the day changes. Deliberately
  // not keyed on `attendees` itself: this only reacts to the day (through
  // `loadBands`, memoised on `span`), and adding an attendee loads its own
  // bands at add-time, in `addAttendee` below -- depending on `attendees`
  // here as well would refetch on every state update this effect itself
  // causes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (attendees.length === 0) return;
    let live = true;
    // The old day's bands are cleared first: until the answer arrives they
    // would be drawn, and used for "N of M free", against the new day.
    setAttendees((prev) => prev.map((x) => ({ ...x, bands: null })));
    for (const a of attendees) {
      const id = a.principal.accountId;
      loadBands(id)
        .then((bands) => {
          if (live) {
            setAttendees((prev) =>
              prev.map((x) => (x.principal.accountId === id ? { ...x, bands } : x)),
            );
          }
        })
        .catch(onError);
    }
    return () => {
      live = false;
    };
  }, [loadBands, onError]);

  const addAttendee = useCallback(() => {
    const email = attendeeInput.trim();
    if (!email) return;
    setAttendeeError(null);
    client
      .resolvePrincipal(email)
      .then((principal) => {
        if (!principal) {
          setAttendeeError(`No one found for ${email}.`);
          return;
        }
        if (identity && principal.accountId === identity.accountId) {
          // Already drawn as your own row; adding yourself would only
          // duplicate it.
          setAttendeeInput('');
          return;
        }
        // Checked on the latest list: Enter and then a blur can start two
        // lookups for the same address.
        setAttendees((prev) =>
          prev.some((a) => a.principal.accountId === principal.accountId)
            ? prev
            : [...prev, { principal, bands: null }],
        );
        setAttendeeInput('');
        loadBands(principal.accountId)
          .then((bands) =>
            setAttendees((prev) =>
              prev.map((a) =>
                a.principal.accountId === principal.accountId ? { ...a, bands } : a,
              ),
            ),
          )
          .catch(onError);
      })
      .catch((err: unknown) => {
        onError(err);
        setAttendeeError('Could not look that person up.');
      });
  }, [attendeeInput, client, identity, loadBands, onError]);

  const removeAttendee = useCallback((accountId: string) => {
    setAttendees((prev) => prev.filter((a) => a.principal.accountId !== accountId));
  }, []);

  const picked = useMemo(() => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':').map(Number);
    if (h === undefined || m === undefined) return null;
    const [y, mo, d] = dateStr.split('-').map(Number);
    if (!y || !mo || !d) return null;
    // The typed wall-clock time, in the picked zone -- not `span.start`'s
    // own fields, which name the *browser's* day (agora#168 review
    // finding: reading them back could silently land on a different
    // calendar day than the one `dateStr` says).
    const start = zonedTimeToUtc(y, mo, d, h, m, timeZone);
    const end = new Date(start.getTime() + duration * 60_000);
    return { start, end };
  }, [dateStr, timeStr, duration, timeZone]);

  const send = useCallback(() => {
    if (!picked || !title.trim() || !ownCalendar || sending || attendeeInput.trim()) return;
    setSending(true);
    // agora#163 (MSG-060): the Agora call link is stored as a
    // `virtualLocations` entry, the JSCalendar-shaped field this package
    // reserves for it, and its text is also duplicated into `description` --
    // neither Apple Calendar nor Outlook renders `virtualLocations` as a
    // join link, so the plain-text copy is what makes it show up there at all.
    const ownDescription = description.trim();
    const uid = crypto.randomUUID();
    const withCall = addCall && callUrl !== undefined;
    // The session and the guest link exist before the event does, so the event
    // is written once, with both links; a call that cannot be made makes no event.
    const prepared: Promise<EventCallSetup | null> =
      withCall && calls
        ? createEventCall(
            calls.client,
            window.location.origin,
            uid,
            picked.start.getTime(),
            picked.end.getTime(),
          )
        : Promise.resolve(null);
    prepared
      .then((setup) => {
        const memberLink = withCall && callUrl ? callUrl(uid) : null;
        const entries = [
          ...(memberLink ? [{ uri: memberLink }] : []),
          ...(setup ? [setup.guestEntry] : []),
        ];
        const lines = [
          ...(memberLink ? [`Join: ${memberLink}`] : []),
          ...(setup ? [`Guests: ${setup.guestUrl}`] : []),
        ];
        const fullDescription = lines.length
          ? [ownDescription, ...lines].filter(Boolean).join('\n\n')
          : ownDescription;
        const created = client.createEvent(
          ownCalendar.id,
          {
            calendarId: ownCalendar.id,
            uid,
            title: title.trim(),
            // The literal calendar day and wall-clock time typed into the
            // form -- not re-derived from `picked.start` -- so this can never
            // land on a different date than `dateStr` says (agora#168 review
            // finding).
            start: `${dateStr}T${timeStr}:00`,
            timeZone,
            duration: toIsoDuration(duration),
            isAllDay: false,
            status: 'confirmed',
            mayInviteOthers,
            mayInviteSelf,
            location: location.trim() || undefined,
            description: fullDescription || undefined,
            virtualLocations: entries.length ? entries : undefined,
            participants: attendees.map((a) => ({
              calendarAddress: a.principal.email,
              name: a.principal.name,
              roles: ['attendee' as const],
              participationStatus: 'needs-action' as const,
            })),
          },
          { sendSchedulingMessages: true },
        );
        // An event that could not be written must not leave its call behind.
        return created.catch(async (failure: unknown) => {
          const made = parseGuestEntry(setup ? [setup.guestEntry] : undefined);
          if (made && calls) {
            await endEventCall(calls.client, made).catch((endError: unknown) =>
              console.error('Could not end the call of an event that was not saved:', endError),
            );
          }
          throw failure;
        });
      })
      .then(onCreated)
      .catch(onError)
      .finally(() => setSending(false));
  }, [
    picked,
    title,
    location,
    description,
    addCall,
    mayInviteOthers,
    mayInviteSelf,
    callUrl,
    calls,
    ownCalendar,
    sending,
    attendeeInput,
    client,
    dateStr,
    timeStr,
    timeZone,
    duration,
    attendees,
    onCreated,
    onError,
  ]);

  // Your own status never joins `unknownNames` -- `ownBands` is only ever
  // busy or free, built from real events rather than an ambiguous empty
  // answer -- but it does count toward `freeCount`/`totalCount`, the same
  // as anyone else's.
  const ownStatus = picked && ownEvents ? statusAt(ownBands, span, picked.start) : null;
  const unknownNames = attendees
    .filter((a) => picked && a.bands && statusAt(a.bands, span, picked.start) === 'unknown')
    .map((a) => a.principal.name ?? a.principal.email);
  const freeCount =
    attendees.filter((a) => picked && statusAt(a.bands ?? [], span, picked.start) === 'free')
      .length + (ownStatus === 'free' ? 1 : 0);
  const totalCount = attendees.length + 1;

  // Why the submit button is greyed out, said next to it.
  const blocker = !title.trim()
    ? 'Give it a name first.'
    : attendeeInput.trim()
      ? 'Press Enter to add that address, or clear it.'
      : !picked
        ? 'Pick a time.'
        : null;

  useEscape(true, onCancel);

  const dayName = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone,
  }).format(span.start);

  return (
    <Panes.Column flex>
      <div className="flex flex-1 flex-col min-h-0 overflow-y-auto box-border stream cal-stream">
        <div className="cal-find">
          <div className="flex flex-col gap-1 col-head">
            <h1 className="h1">New event</h1>
            <p className="lead m-0 text-lg text-muted">
              {dayName}, against what everybody is doing.
            </p>
          </div>

          <div className="cal-find-form">
            <Field label="What is it">
              {(field) => (
                <Input
                  {...field}
                  placeholder="A name for the event"
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      send();
                    }
                  }}
                  block
                />
              )}
            </Field>
            <div className="flex gap-4 flex-wrap cal-find-row">
              <Field label="Where">
                {(field) => (
                  <Input
                    {...field}
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="A room or an address (optional)"
                    block
                  />
                )}
              </Field>
            </div>
            {callUrl && <Toggle label="Add a call" checked={addCall} onChange={setAddCall} />}
            <Toggle
              label="Guests can invite others"
              checked={mayInviteOthers}
              onChange={setMayInviteOthers}
            />
            <Toggle label="Anyone can join" checked={mayInviteSelf} onChange={setMayInviteSelf} />
            <Field label="Description">
              {(field) => (
                <Textarea
                  {...field}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
            </Field>
            <div className="flex gap-4 flex-wrap cal-find-row">
              <Field label="Who">
                {(field) => (
                  <Input
                    {...field}
                    value={attendeeInput}
                    onChange={(e) => setAttendeeInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAttendee();
                      }
                    }}
                    onBlur={addAttendee}
                    placeholder="An email address, then Enter"
                    block
                  />
                )}
              </Field>
            </div>
            {attendeeError && (
              <p className="cal-error text-bad" role="alert">
                {attendeeError}
              </p>
            )}
            <div className="flex gap-2 flex-wrap cal-find-chips">
              {attendees.map((a) => (
                <Identity
                  key={a.principal.accountId}
                  name={a.principal.name}
                  address={a.principal.email}
                  kind="chip"
                  onRemove={() => removeAttendee(a.principal.accountId)}
                />
              ))}
            </div>
            <div className="flex gap-4 flex-wrap cal-find-row">
              <Field label="Day">
                {(field) => (
                  <>
                    <Input
                      {...field}
                      type="date"
                      value={dateStr}
                      onChange={(e) => setDateStr(e.target.value)}
                      block
                    />
                    {/* The browser writes the date in its own order; this is the
                        page's own wording of the same day. */}
                    <span className="text-xs text-muted">{dayName}</span>
                  </>
                )}
              </Field>
              <Field label="Time">
                {(field) => (
                  <Input
                    {...field}
                    type="time"
                    value={timeStr}
                    onChange={(e) => setTimeStr(e.target.value)}
                    block
                  />
                )}
              </Field>
              <Field label="How long">
                {() => (
                  <Segmented
                    label="How long"
                    size="sm"
                    value={String(duration)}
                    onChange={(value) => setDuration(Number(value) as (typeof DURATIONS)[number])}
                  >
                    {DURATIONS.map((d) => (
                      <SegmentedOption key={d} value={String(d)}>
                        {d < 60 ? `${d} min` : `${d / 60} hour`}
                      </SegmentedOption>
                    ))}
                  </Segmented>
                )}
              </Field>
              <Field label="Time zone">
                <Select
                  value={timeZone}
                  onChange={(e) => setTimeZone(e.target.value)}
                  searchable
                  block
                >
                  {/* The runtime's list leaves out `UTC` and alias names a
                      browser can still report as its own zone; without its
                      own option the picker would show nothing selected. */}
                  {(TIME_ZONES.includes(timeZone) ? TIME_ZONES : [timeZone, ...TIME_ZONES]).map(
                    (tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ),
                  )}
                </Select>
              </Field>
            </div>
          </div>

          {/* Names the zone the grid below and the free/busy answer are
              both in now -- the picked zone, not the browser's (agora#168
              review finding). */}
          <p className="text-xs text-muted">Hours below are {timeZone} time.</p>
          <section className="card cal-card">
            {/* Your own row first, drawn from `ownBands` -- see the comment
                above it. Never through `FreeBusy`'s own `Identity`, which
                ties a row's initials and its shown name to the one `name`
                it is given: the avatar wants your real initials, the row
                wants to read "You", and `Identity` cannot do both from one
                string. */}
            {/* The hours head the card, above your own row, as the board draws
                them; `FreeBusy`'s own copy of this row is hidden in CSS so the
                hours appear once. */}
            <div
              className="flex items-center gap-3 pb-1 cal-fb-row cal-fb-hours"
              aria-hidden="true"
            >
              <div className="cal-fb-who" />
              <div className="flex flex-1 gap-0.5 h-6 cal-fb-bands">
                {hourLabels.map((hour) => (
                  <span key={hour} className="flex-1 text-muted text-2xs font-strong cal-fb-hour">
                    {hour}
                  </span>
                ))}
              </div>
            </div>
            <OwnBandRow name={meName} address={identity?.calendarAddress ?? ''} bands={ownBands} />
            {/* `FreeBusy` only ever draws `unknown` as its own hatch -- "not
                shared with you" -- so a person still loading never reaches
                it: feeding it a loading row would hatch exactly like one
                who has genuinely shared nothing, which is the wrong
                signal. Loaded people go to the real component; a person
                still loading gets a plain row of this section's own,
                matching `FreeBusy`'s layout, until their bands arrive. */}
            <FreeBusy
              label={`Free and busy, ${new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone }).format(span.start)}`}
              hours={hourLabels}
              people={attendees
                .filter((a) => a.bands !== null)
                .map(
                  (a): FreeBusyPerson => ({
                    name: a.principal.name ?? a.principal.email,
                    address: a.principal.email,
                    detail:
                      a.bands?.length === 1 && a.bands[0]?.status === 'unknown'
                        ? 'Has not shared anything'
                        : 'Shares their calendar',
                    slots: a.bands ?? [],
                  }),
                )}
            />
            {attendees
              .filter((a) => a.bands === null)
              .map((a) => (
                <div
                  key={a.principal.accountId}
                  className="flex items-center gap-3 py-2 px-0 cal-fb-row"
                >
                  <div className="cal-fb-who">
                    <Identity name={a.principal.name} address={a.principal.email} size="sm" />
                    <span className="text-xs text-muted pl-8">Loading…</span>
                  </div>
                  <span className="cal-fb-skeleton" aria-hidden="true" />
                </div>
              ))}
          </section>

          {picked && (
            <div className="card cal-tint p-4 cal-picked">
              <Icon name="calendar" size={20} />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-bold cal-strong">
                  {pickedTimeLabel(picked.start, picked.end, timeZone)}
                </span>
                <span className="text-xs text-muted">
                  {unknownNames.length > 0
                    ? `${freeCount} of ${totalCount} known to be free. ${unknownNames.join(', ')} ${
                        unknownNames.length === 1 ? 'has' : 'have'
                      } shared nothing, so nobody knows — they get the invitation and answer it themselves.`
                    : totalCount === 1
                      ? `You are ${freeCount === 1 ? 'free' : 'busy'} at this time.`
                      : `${freeCount} of ${totalCount} are free at this time.`}
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-3 items-center cal-find-actions">
            <Button
              kind="primary"
              busy={sending}
              disabled={!picked || !title.trim() || !ownCalendar || attendeeInput.trim() !== ''}
              onPress={send}
            >
              <Icon name="send" size={16} />
              {attendees.length > 0 ? 'Send the invitation' : 'Create the event'}
            </Button>
            <Button kind="default" onPress={onCancel}>
              Cancel
            </Button>
            {blocker && <span className="text-xs text-muted">{blocker}</span>}
          </div>
          {!ownCalendar && (
            <p className="cal-error text-bad" role="alert">
              There is no calendar to create this on.
            </p>
          )}
        </div>
      </div>
    </Panes.Column>
  );
}

/** The next whole hour, as a time field's `HH:MM` (23:00 at the latest, so
 *  the default never lands on tomorrow's midnight). */
function defaultTime(): string {
  return `${String(Math.min(new Date().getHours() + 1, 23)).padStart(2, '0')}:00`;
}

/** Your own free/busy row -- the canvas's own `person(..., 'You', ...)`:
 *  the avatar's initials come from your real name (`Avatar`, the same
 *  initials the mail reply box and the account chip draw), the row itself
 *  always reads "You", and there is no "mine" chip repeating it a second
 *  time. */
function OwnBandRow({
  name,
  address,
  bands,
}: {
  name: string | null;
  address: string;
  bands: readonly FreeBusySlot[];
}) {
  return (
    <div className="flex items-center gap-3 py-2 px-0 cal-fb-row">
      <div className="cal-fb-who">
        <Avatar name={name} seed={address} size="sm" />
        <span className="text-sm font-strong">You</span>
      </div>
      <div className="flex flex-1 gap-0.5 h-6 cal-fb-bands">
        {bands.map((slot, i) => (
          // A fixed, ordered run across one day, never reordered -- the
          // same call `FreeBusy`'s own slots make.
          // biome-ignore lint/suspicious/noArrayIndexKey: see above
          <span key={i} className={`cal-fb-band ${slot.status}`} style={{ flex: slot.span }}>
            <span className="sr-only">{slot.status === 'busy' ? 'Busy' : 'Free'}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
